import Sentry from "@similarweb/sentry-logger";

import {
    addRevealingContactIds,
    removeRevealingContactIds,
    updateRevealedContactsByUniqId,
} from "app/pages/sales-intelligence/sub-modules/common/store/action-creators";
import type { ContactsDataSource } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext.types";
import type { ThunkActionCommon, ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import type { Integration } from "../../management/types";
import { fromUniqueIdToPair, toUniqueContactId } from "../helpers/common";
import { IDLE_PHASE } from "../helpers/exportFlow";
import trackingService from "../services/tracking/trackingService";
import type { ContactSearchEntry, RevealedContactDto } from "../types/common";
import type { ContactIdCompanyPair } from "../types/dto";
import type { ExportToExcelFn, FetchTopNContactsFn } from "../types/exportFlow";

import {
    resetExportToCrm,
    setExportFlowOrigin,
    setExportFlowPhase,
    setIsActionPanelActivated,
    setPendingRevealInfo,
    setRequestedContactsForExport,
    tableRowsSelectionChangedAction,
    tableRowsSelectionToggledAction,
} from "./action-creators";
import { extractContactsPreview, isFailedFetchingPreviewAction } from "./action-utils";
import {
    exportContactsToCrmAsContactsThunk,
    exportContactsToCrmAsLeadsThunk,
    fetchExportToCrmPreviewThunk,
    showCombinedRevealExportToast,
} from "./effects";
import { revealSelectedContactsByIdSilentThunk } from "./effects-reveal";
import {
    selectCrmExportPairs,
    selectEntityToExportContactsAs,
    selectExportVariant,
    selectCrmToExportContacts,
} from "./selectors";

/**
 * Redux-only cleanup. Does NOT call onDeactivate — the hook's useEffect
 * detects the idle transition and calls the current component's onDeactivate.
 */
export function resetExportFlowStateThunk(): ThunkActionCommon {
    return (dispatch) => {
        dispatch(setExportFlowPhase(IDLE_PHASE));
        dispatch(setExportFlowOrigin(null));
        dispatch(setIsActionPanelActivated(false));
        dispatch(resetExportToCrm());
        dispatch(tableRowsSelectionChangedAction([]));
        dispatch(tableRowsSelectionToggledAction(false));
    };
}

async function executeExport({
    dispatch,
    getState,
    exportToExcel,
    countries,
    pairs,
    isSilent,
}: {
    dispatch: ThunkDispatchCommon;
    getState: ThunkGetState;
    exportToExcel: ExportToExcelFn;
    countries: readonly number[];
    pairs: readonly ContactIdCompanyPair[];
    isSilent?: boolean;
}): Promise<{ exportedCount: number; exportHistoryUrl?: string }> {
    const state = getState();
    const exportVariant = selectExportVariant(state);
    const exportAs = selectEntityToExportContactsAs(state);

    if (exportVariant === "crm" && exportAs === "lead") {
        return dispatch(exportContactsToCrmAsLeadsThunk(pairs, countries, isSilent));
    }

    return exportToExcel(pairs, isSilent);
}

async function exportOnlyRevealed({
    dispatch,
    getState,
    exportToExcel,
    countries,
    revealedInSelection,
}: {
    dispatch: ThunkDispatchCommon;
    getState: ThunkGetState;
    exportToExcel: ExportToExcelFn;
    countries: readonly number[];
    revealedInSelection: readonly ContactSearchEntry[];
}) {
    dispatch(setRequestedContactsForExport(revealedInSelection));
    const revealedExportPairs = revealedInSelection.map((c) => fromUniqueIdToPair(toUniqueContactId(c)));

    if (revealedExportPairs.length === 0) {
        return;
    }

    await executeExport({ dispatch, getState, exportToExcel, countries, pairs: revealedExportPairs });
}

export function submitExportThunk(
    exportToExcel: ExportToExcelFn,
    countries: readonly number[],
    exportContacts: ContactSearchEntry[],
): ThunkActionCommon<Promise<void>> {
    return async (dispatch, getState) => {
        const state = getState();
        const exportVariant = selectExportVariant(state);
        const exportAs = selectEntityToExportContactsAs(state);
        const integration = selectCrmToExportContacts(state);

        dispatch(setRequestedContactsForExport(exportContacts));

        const pairs = exportContacts.map((c) => fromUniqueIdToPair(toUniqueContactId(c)));

        if (exportVariant === "crm" && exportAs === "contact" && integration) {
            dispatch(setExportFlowPhase({ type: "matchingCrmContacts", variant: "crm", integration }));
            await dispatch(startCrmContactExportThunk(countries));
            return;
        }

        try {
            if (exportVariant === "crm" && exportAs === "lead") {
                dispatch(setExportFlowPhase({ type: "exportingCrmLeads", variant: "crm" }));
            } else {
                dispatch(setExportFlowPhase({ type: "exportingExcel", variant: "excel" }));
            }
            await executeExport({ dispatch, getState, exportToExcel, countries, pairs });
        } finally {
            dispatch(resetExportFlowStateThunk());
        }
    };
}

async function handleExportAfterReveal({
    dispatch,
    getState,
    exportToExcel,
    fetchTopNContacts,
    countries,
    revealResult,
    meta,
    requestedContactsForExport,
}: {
    dispatch: ThunkDispatchCommon;
    getState: ThunkGetState;
    exportToExcel: ExportToExcelFn;
    fetchTopNContacts: FetchTopNContactsFn;
    countries: readonly number[];
    revealResult: {
        revealedCount: number;
        needsRefetch?: boolean;
        revealedContactIds?: readonly string[];
    };
    meta: { unrevealedCount: number; requestedCount: number; alreadyRevealedCount: number };
    requestedContactsForExport: readonly ContactSearchEntry[];
}) {
    const state = getState();
    const exportVariant = selectExportVariant(state);
    const exportAs = selectEntityToExportContactsAs(state);
    const integration = selectCrmToExportContacts(state);

    const { unrevealedCount, requestedCount, alreadyRevealedCount } = meta;
    const newlyRevealedCount = revealResult.revealedCount;
    const isFullRevealSuccess = newlyRevealedCount === unrevealedCount;

    if (revealResult.needsRefetch && exportVariant === "crm" && exportAs === "contact") {
        const freshContacts = await fetchTopNContacts(requestedCount);
        const revealedPrivateData = freshContacts
            .filter((c) => c.isRevealed && c.privateData)
            .map((c) => c.privateData as RevealedContactDto);

        if (revealedPrivateData.length > 0) {
            dispatch(updateRevealedContactsByUniqId(revealedPrivateData));
        }
    }

    const newlyRevealedIds = revealResult.revealedContactIds ?? [];
    const contactsToExport = requestedContactsForExport.filter((c) => c.isRevealed || newlyRevealedIds.includes(c.id));
    const exportPairs = contactsToExport.map((c) => fromUniqueIdToPair(toUniqueContactId(c)));

    if (exportVariant === "crm" && exportAs === "contact" && integration) {
        dispatch(
            setPendingRevealInfo({
                revealedCount: newlyRevealedCount,
                unrevealedCount,
                requestedCount,
                isFullRevealSuccess,
            }),
        );
        dispatch(setRequestedContactsForExport(contactsToExport));
        await executeExport({ dispatch, getState, exportToExcel, countries, pairs: exportPairs });

        dispatch(setExportFlowPhase({ type: "matchingCrmContacts", variant: "crm", integration }));
        await dispatch(startCrmContactExportThunk(countries));
        return;
    }

    if (exportVariant === "crm" && exportAs === "lead") {
        dispatch(setExportFlowPhase({ type: "exportingCrmLeads", variant: "crm" }));
    } else {
        dispatch(setExportFlowPhase({ type: "exportingExcel", variant: "excel" }));
    }

    const { exportedCount, exportHistoryUrl } = await executeExport({
        dispatch,
        getState,
        exportToExcel,
        countries,
        pairs: exportPairs,
        isSilent: true,
    });

    const totalContacts = alreadyRevealedCount + newlyRevealedCount;

    dispatch(
        showCombinedRevealExportToast(
            {
                revealedCount: newlyRevealedCount,
                unrevealedCount,
                requestedCount,
                isFullRevealSuccess,
            },
            exportedCount,
            totalContacts,
            exportHistoryUrl,
        ),
    );

    dispatch(resetExportFlowStateThunk());
}

type ConfirmRevealAndExportParams = {
    exportToExcel: ExportToExcelFn;
    fetchTopNContacts: FetchTopNContactsFn;
    revealMultiple: ContactsDataSource["revealMultiple"];
    countries: readonly number[];
    remainingCredits: number;
    revealedInSelection: readonly ContactSearchEntry[];
    contactsToReveal: readonly ContactSearchEntry[];
    requestedContactsForExport: readonly ContactSearchEntry[];
    revealMaxItem: number;
};

async function performCrmContactExport({
    dispatch,
    countries,
    integration,
}: {
    dispatch: ThunkDispatchCommon;
    countries: readonly number[];
    integration: Integration;
}) {
    try {
        const result = await dispatch(exportContactsToCrmAsContactsThunk(countries));
        trackingService.trackContactsExportToCrm(integration, result.contacts, result.exportedCount);
        dispatch(resetExportToCrm());
    } finally {
        dispatch(resetExportFlowStateThunk());
    }
}

export function confirmRevealAndExportThunk({
    exportToExcel,
    fetchTopNContacts,
    revealMultiple,
    countries,
    remainingCredits,
    revealedInSelection,
    contactsToReveal,
    requestedContactsForExport,
    revealMaxItem,
}: ConfirmRevealAndExportParams): ThunkActionCommon<Promise<void>> {
    return async (dispatch, getState) => {
        const state = getState();
        const exportVariant = selectExportVariant(state);
        const exportAs = selectEntityToExportContactsAs(state);
        const integration = selectCrmToExportContacts(state);

        const variant = exportVariant ?? "excel";
        const crmConfig =
            exportVariant === "crm" && integration && exportAs ? { integration, entity: exportAs } : undefined;

        dispatch(setExportFlowPhase({ type: "revealing", variant, crmConfig }));
        dispatch(setRequestedContactsForExport([]));

        try {
            if (remainingCredits <= 0) {
                if (
                    exportVariant === "crm" &&
                    exportAs === "contact" &&
                    integration &&
                    revealedInSelection.length > 0
                ) {
                    dispatch(setRequestedContactsForExport(revealedInSelection));
                    dispatch(setExportFlowPhase({ type: "matchingCrmContacts", variant: "crm", integration }));
                    await dispatch(startCrmContactExportThunk(countries));
                } else {
                    await exportOnlyRevealed({
                        dispatch,
                        getState,
                        exportToExcel,
                        countries,
                        revealedInSelection,
                    });
                    dispatch(resetExportFlowStateThunk());
                }
                return;
            }

            const revealLimit = Math.min(remainingCredits, revealMaxItem);
            const limitedContacts =
                revealLimit < contactsToReveal.length ? contactsToReveal.slice(0, revealLimit) : contactsToReveal;
            const unrevealedCount = limitedContacts.length;
            const requestedCount = requestedContactsForExport.length;
            const alreadyRevealedCount = requestedContactsForExport.length - contactsToReveal.length;

            const uniqueIdsForReveal = limitedContacts.map((c) => toUniqueContactId(c));
            const unrevealedIds = limitedContacts.map((c) => c.id);

            if (unrevealedIds.length > 0) {
                dispatch(addRevealingContactIds(unrevealedIds));
            }

            try {
                const revealResult = await dispatch(
                    revealSelectedContactsByIdSilentThunk(uniqueIdsForReveal, revealMultiple),
                );

                await handleExportAfterReveal({
                    dispatch,
                    getState,
                    exportToExcel,
                    fetchTopNContacts,
                    countries,
                    revealResult,
                    meta: { unrevealedCount, requestedCount, alreadyRevealedCount },
                    requestedContactsForExport,
                });
            } finally {
                if (unrevealedIds.length > 0) {
                    dispatch(removeRevealingContactIds(unrevealedIds));
                }
            }
        } catch (error) {
            Sentry.errorTracker({ message: "confirmRevealAndExportThunk error", e: error });
            dispatch(resetExportFlowStateThunk());
        }
    };
}

export function startCrmContactExportThunk(countries: readonly number[]): ThunkActionCommon<Promise<void>> {
    return async (dispatch, getState) => {
        const state = getState();
        const integration = selectCrmToExportContacts(state);
        const pairs = selectCrmExportPairs(state);

        if (!integration) {
            dispatch(resetExportFlowStateThunk());
            return;
        }

        dispatch(setExportFlowPhase({ type: "matchingCrmContacts", variant: "crm", integration }));

        try {
            const resultAction = await dispatch(fetchExportToCrmPreviewThunk(pairs, integration));

            if (isFailedFetchingPreviewAction(resultAction)) {
                dispatch(resetExportFlowStateThunk());
                return;
            }

            const contactsPreview = extractContactsPreview(resultAction);
            const shouldAutoExport =
                contactsPreview.length > 0 &&
                contactsPreview.every((contact) => contact.matchedCompanies.length === 1 && !contact.isExistAsLead);

            if (shouldAutoExport) {
                dispatch(setExportFlowPhase({ type: "exportingCrmContacts", variant: "crm", integration }));
                await performCrmContactExport({ dispatch, countries, integration });
            } else {
                dispatch(setExportFlowPhase({ type: "previewingCrmContacts", variant: "crm", integration }));
            }
        } catch (error) {
            Sentry.errorTracker({ message: "startCrmContactExportThunk error", e: error });
            dispatch(resetExportFlowStateThunk());
        }
    };
}

export function confirmCrmPreviewExportThunk(countries: readonly number[]): ThunkActionCommon<Promise<void>> {
    return async (dispatch, getState) => {
        const state = getState();
        const integration = selectCrmToExportContacts(state);

        if (!integration) {
            dispatch(resetExportFlowStateThunk());
            return;
        }

        dispatch(setExportFlowPhase({ type: "exportingCrmContacts", variant: "crm", integration }));
        await performCrmContactExport({ dispatch, countries, integration });
    };
}

export function cancelCrmExportThunk(): ThunkActionCommon {
    return (dispatch, getState) => {
        const state = getState();
        const integration = selectCrmToExportContacts(state);

        if (integration) {
            trackingService.trackCancelExportWhileReviewing(integration);
        }

        dispatch(resetExportFlowStateThunk());
    };
}

export function cancelCrmLoadingThunk(): ThunkActionCommon {
    return (dispatch, getState) => {
        const state = getState();
        const integration = selectCrmToExportContacts(state);

        if (integration) {
            trackingService.trackCancelExportWhileLoading(integration);
        }

        dispatch(resetExportFlowStateThunk());
    };
}
